"use client";

import { useCallback, useRef, useState } from "react";
import Icon from "@/components/Icon";
import Text from "@/components/Text";
import Button from "@/components/Button";
import DropdownMenu from "@/components/DropdownMenu";
import classNames from "@/helpers/classNames";
import type { FilterOptions } from "@/tools/medusa";
import type { ActiveFilters } from "@/components/CollectionFilterPanel";
import styles from "./styles.module.scss";

interface CollectionFilterBarProps {
  filterOptions: FilterOptions;
  activeFilters: ActiveFilters;
  onOpenFilters: () => void;
  onRemoveCategory: (categoryId: string) => void;
  onRemoveType: (typeId: string) => void;
  onResetPrice: () => void;
  onSortChange: (sort: string) => void;
  className?: string;
}

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "price_asc", label: "Price: Low to High" },
  { value: "price_desc", label: "Price: High to Low" },
  { value: "title_asc", label: "A-Z" },
];

const CollectionFilterBar = (props: CollectionFilterBarProps) => {
  const {
    filterOptions,
    activeFilters,
    onOpenFilters,
    onRemoveCategory,
    onRemoveType,
    onResetPrice,
    onSortChange,
    className,
  } = props;

  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  const activeCategoryNames = activeFilters.categoryIds
    .map((id) => filterOptions.categories.find((c) => c.id === id))
    .filter(Boolean);

  const activeTypeNames = activeFilters.typeIds
    .map((id) => filterOptions.types.find((t) => t.id === id))
    .filter(Boolean);

  const hasPriceFilter =
    activeFilters.priceMin !== filterOptions.priceMin ||
    activeFilters.priceMax !== filterOptions.priceMax;

  const priceLabel = hasPriceFilter
    ? `$${activeFilters.priceMin.toFixed(0)} – $${activeFilters.priceMax.toFixed(0)}`
    : null;

  const currentSortLabel =
    SORT_OPTIONS.find((o) => o.value === activeFilters.sort)?.label ?? "Newest";

  const activeFilterCount =
    activeCategoryNames.length + activeTypeNames.length + (hasPriceFilter ? 1 : 0);

  const handleSortSelect = useCallback(
    (value: string) => {
      onSortChange(value);
      setSortOpen(false);
    },
    [onSortChange],
  );

  return (
    <div className={classNames(styles.bar, className)}>
      <button
        type="button"
        className={styles.mobileFilters}
        onClick={onOpenFilters}
        // Named by what it shows, "2 Filters selected", which a hidden "Open filters" label
        // replaced (WCAG 2.5.3). That it opens the filters is what `aria-haspopup` says.
        aria-haspopup="dialog"
      >
        <span className={styles.mobileFiltersLeft}>
          <Icon title="slidersHorizontal" size="md" />
          <Text
            text={`${activeFilterCount} ${activeFilterCount === 1 ? "Filter" : "Filters"} selected`}
            size="md"
            variant="label"
          />
        </span>
        <Icon className={styles.mobileFiltersLeftChevron} title="chevronDown" size="sm" />
      </button>

      <div className={styles.left}>
        <Button
          className={styles.filterButton}
          onClick={onOpenFilters}
          iconHover={false}
          icon="slidersHorizontal"
          iconSize="lg"
          theme="outline-black"
        >
          <Text text="Filters" size="md" variant="label" />
        </Button>
        {(activeCategoryNames.length > 0 || activeTypeNames.length > 0 || hasPriceFilter) && (
          <div className={styles.pills}>
            {hasPriceFilter && priceLabel && (
              <Button
                type="button"
                className={styles.filterButton}
                onClick={onResetPrice}
                ariaLabel="Remove price range filter"
                iconHover={false}
                icon="close"
                iconSize="md"
                theme="outline-black"
              >
                <Text text={priceLabel} size="md" variant="label" />
              </Button>
            )}
            {activeCategoryNames.map(
              (cat) =>
                cat && (
                  <button
                    key={cat.id}
                    type="button"
                    className={styles.pill}
                    onClick={() => onRemoveCategory(cat.id)}
                    aria-label={`Remove ${cat.value} filter`}
                  >
                    <Text text={cat.value} size="md" variant="label" />
                    <Icon title="close" size="sm" />
                  </button>
                ),
            )}
            {activeTypeNames.map(
              (type) =>
                type && (
                  <button
                    key={type.id}
                    type="button"
                    className={styles.pill}
                    onClick={() => onRemoveType(type.id)}
                    aria-label={`Remove ${type.value} filter`}
                  >
                    <Text text={type.value} size="md" variant="label" />
                    <Icon title="close" size="sm" />
                  </button>
                ),
            )}
          </div>
        )}
      </div>

      <div className={styles.right} ref={sortRef}>
        <button
          type="button"
          className={styles.sortTrigger}
          onClick={() => setSortOpen((prev) => !prev)}
          aria-expanded={sortOpen}
          aria-haspopup="listbox"
        >
          <span className={styles.sortLabel}>
            <Icon title="funnelSimple" size="md" />
            <Text text="Sort by:" size="md" variant="label" />
            <Icon className={styles.sortChevronMobile} title="chevronDown" size="sm" />
          </span>
          <span className={styles.sortValue}>
            <Text text={currentSortLabel} size="md" variant="label" color="themeFgMuted" />
            <Icon className={styles.sortChevronDesktop} title="chevronDown" size="sm" />
          </span>
        </button>
        <DropdownMenu
          open={sortOpen}
          onClose={() => setSortOpen(false)}
          containerRef={sortRef}
          align="right"
          ariaLabel="Sort options"
          minWidth={180}
        >
          {SORT_OPTIONS.map((option) => (
            <DropdownMenu.Option
              key={option.value}
              selected={activeFilters.sort === option.value}
              onClick={() => handleSortSelect(option.value)}
            >
              <Text text={option.label} size="md" variant="label" />
            </DropdownMenu.Option>
          ))}
        </DropdownMenu>
      </div>
    </div>
  );
};

export default CollectionFilterBar;
